import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";

// ─── Rich gallery card (thumbnail with zoom icon) ────────────────────────────
export const GalleryCard = ({ item, onOpen }) => {
  const images = item.images || [];
  const firstImage = images[0];

  return (
    <div
      className="overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer group"
      data-testid={`gallery-item-${item.id}`}
      onClick={onOpen}
    >
      <div className="relative aspect-square overflow-hidden bg-white">
        {firstImage && (
          <img
            src={firstImage}
            alt={`${item.label} 1`}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/20 transition-colors duration-300 flex items-center justify-center pointer-events-none">
          <ZoomIn
            size={28}
            strokeWidth={1.2}
            className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          />
        </div>
      </div>
      {images.length > 1 && (
        <div className="px-4 py-2 bg-white flex justify-end">
          <span className="text-[10px] text-ink/40 tracking-wide">1 / {images.length}</span>
        </div>
      )}
    </div>
  );
};

// ─── Rich lightbox (patient nav + multi-image per item + thumbnails) ──────────
export const GalleryLightbox = ({
  item,
  onClose,
  onPrevPatient,
  onNextPatient,
  hasPrevPatient,
  hasNextPatient,
  patientIndex,
  totalPatients,
}) => {
  const [imgIdx, setImgIdx] = useState(0);
  const images = item.images || [];

  useEffect(() => {
    setImgIdx(0);
  }, [item]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && images.length > 1)
        setImgIdx((i) => (i - 1 + images.length) % images.length);
      if (e.key === "ArrowRight" && images.length > 1)
        setImgIdx((i) => (i + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, images.length]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[100] bg-[#808080] flex flex-col" onClick={onClose}>
      {/* Top bar — image counter + label + total smiles + close */}
      <div
        className="grid grid-cols-[1fr_auto_1fr] items-center px-6 py-4 flex-shrink-0 gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-white font-mono">
            {imgIdx + 1} / {images.length}
          </div>
          <div className="text-sm text-white mt-0.5">{item.label}</div>
        </div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/85 whitespace-nowrap text-center">
          Total Smiles {patientIndex + 1} / {totalPatients}
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="w-9 h-9 border border-white/35 bg-white/20 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors"
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.4} />
          </button>
        </div>
      </div>

      <div
        className="flex-1 overflow-hidden flex items-center justify-center relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left row — Previous Smile + image prev arrow */}
        <div className="fixed left-2 top-1/2 -translate-y-1/2 z-[110] flex flex-row items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onPrevPatient}
            disabled={!hasPrevPatient}
            className="flex flex-col items-center gap-1 px-2 py-3 border border-white/35 bg-black/35 text-white hover:border-gold hover:text-gold transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
            aria-label="Previous patient"
          >
            <ChevronLeft size={18} strokeWidth={1.4} />
            <span className="text-[9px] tracking-[0.15em] uppercase">Previous Smile</span>
          </button>
          {images.length > 1 && (
            <button
              onClick={() => setImgIdx((i) => (i - 1 + images.length) % images.length)}
              className="w-11 h-11 border border-white/35 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors bg-black/35"
              aria-label="Previous image"
            >
              <ChevronLeft size={20} strokeWidth={1.4} />
            </button>
          )}
        </div>

        <div className="w-full h-full flex items-center justify-center px-24 py-6">
          <img
            src={images[imgIdx]}
            alt={`${item.label} ${imgIdx + 1}`}
            className="max-h-[72vh] w-auto max-w-full object-contain"
            draggable={false}
          />
        </div>

        {/* Right row — image next arrow + Next Smile */}
        <div className="fixed right-2 top-1/2 -translate-y-1/2 z-[110] flex flex-row items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {images.length > 1 && (
            <button
              onClick={() => setImgIdx((i) => (i + 1) % images.length)}
              className="w-11 h-11 border border-white/35 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors bg-black/35"
              aria-label="Next image"
            >
              <ChevronRight size={20} strokeWidth={1.4} />
            </button>
          )}
          <button
            onClick={onNextPatient}
            disabled={!hasNextPatient}
            className="flex flex-col items-center gap-1 px-2 py-3 border border-white/35 bg-black/35 text-white hover:border-gold hover:text-gold transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
            aria-label="Next patient"
          >
            <ChevronRight size={18} strokeWidth={1.4} />
            <span className="text-[9px] tracking-[0.15em] uppercase">Next Smile</span>
          </button>
        </div>
      </div>

      <div className="flex-shrink-0 px-4 md:px-6 py-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center mb-3">
          <span className="inline-block px-3 py-1 text-xs tracking-widest uppercase border border-white/70 text-white">
            {item.category}
          </span>
        </div>
        {images.length > 1 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1 justify-start md:justify-center">
            {images.map((image, i) => (
              <button
                key={image + i}
                onClick={() => setImgIdx(i)}
                aria-label={`View image ${i + 1}`}
                className={`relative w-14 h-14 md:w-16 md:h-16 shrink-0 overflow-hidden border transition-all duration-300 ${
                  i === imgIdx
                    ? "border-gold ring-1 ring-gold/70"
                    : "border-white/35 hover:border-white/70"
                }`}
              >
                <img
                  src={image}
                  alt={`${item.label} thumbnail ${i + 1}`}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
                {i !== imgIdx && <div className="absolute inset-0 bg-black/30" />}
              </button>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

// ─── API constants (shared with SmileGalleryPage) ────────────────────────────
const SMILE_GALLERY_API_URL = "https://api.galleriadentalgrp.com/api/public/smile-gallery";
const SMILE_GALLERY_API_TOKEN =
  process.env.REACT_APP_TESTIMONIALS_API_TOKEN || "gd_23ec6c2817c94efb85ac1308d47b4a8c";

const getFirstString = (item, keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

const normalizeItem = (item, index) => {
  const orderedImages = Array.isArray(item?.imageDetails)
    ? item.imageDetails
        .filter((d) => d && typeof d.url === "string" && d.url.trim() && Number.isFinite(d.order))
        .sort((a, b) => a.order - b.order)
        .map((d) => d.url.trim())
    : [];

  const images = orderedImages.length > 0
    ? orderedImages
    : Array.isArray(item?.images)
    ? item.images.filter((u) => typeof u === "string" && u.trim())
    : [];

  return {
    id: item?._id || item?.id || `item-${index}`,
    category: getFirstString(item, ["category"]) || "Smile Gallery",
    label: getFirstString(item, ["label", "title", "description"]) || `Gallery ${index + 1}`,
    images,
  };
};

// ─── Simple flat-image grid + lightbox (for plain image arrays) ───────────────
export const SmileGalleryGrid = ({ images = [], fromApi = false, limit = 6, order = "asc", className = "" }) => {
  const [apiItems, setApiItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const hasHistoryEntry = useRef(false);

  // API fetch mode
  useEffect(() => {
    if (!fromApi) return;
    const controller = new AbortController();
    (async () => {
      try {
        const params = new URLSearchParams({ limit: String(limit * 3), sort: "createdAt", order });
        const res = await fetch(`${SMILE_GALLERY_API_URL}?${params}`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${SMILE_GALLERY_API_TOKEN}` },
        });
        const payload = await res.json();
        if (!res.ok || !payload?.success) return;
        const raw = Array.isArray(payload?.data) ? payload.data : [];
        const sorted = raw.slice().sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return order === "desc" ? dateB - dateA : dateA - dateB;
        });
        const items = sorted
          .map(normalizeItem)
          .filter((it) => it.images.length > 0)
          .slice(0, limit);
        setApiItems(items);
      } catch (e) {
        if (e.name !== "AbortError") setApiItems([]);
      }
    })();
    return () => controller.abort();
  }, [fromApi, limit, order]);

  const closeLightbox = useCallback(() => {
    if (hasHistoryEntry.current) {
      hasHistoryEntry.current = false;
      window.history.back();
      return;
    }
    setLightboxIndex(null);
  }, []);

  const closePopup = useCallback(() => {
    if (hasHistoryEntry.current) {
      hasHistoryEntry.current = false;
      window.history.back();
      return;
    }
    setActiveImage(null);
  }, []);

  // History entry for API lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    window.history.pushState({ ...(window.history.state || {}), smileGalleryPopupOpen: true }, "");
    hasHistoryEntry.current = true;
    const onPop = () => {
      if (!hasHistoryEntry.current) return;
      hasHistoryEntry.current = false;
      setLightboxIndex(null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [lightboxIndex]);

  // Keyboard + history entry for simple image popup
  useEffect(() => {
    if (!activeImage) return;
    const onKeyDown = (e) => { if (e.key === "Escape") closePopup(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeImage, closePopup]);

  useEffect(() => {
    if (!activeImage) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.history.pushState({ ...(window.history.state || {}), smileGalleryPopupOpen: true }, "");
    hasHistoryEntry.current = true;
    const onPopState = () => {
      if (!hasHistoryEntry.current) return;
      hasHistoryEntry.current = false;
      setActiveImage(null);
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("popstate", onPopState);
    };
  }, [activeImage]);

  // API mode — render GalleryCard + GalleryLightbox
  if (fromApi) {
    if (!apiItems.length) {
      return (
        <div className={className}>
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="aspect-square bg-ink/5 animate-pulse" />
          ))}
        </div>
      );
    }
    return (
      <>
        {lightboxIndex !== null && (
          <GalleryLightbox
            item={apiItems[lightboxIndex]}
            onClose={closeLightbox}
            onPrevPatient={() => setLightboxIndex((i) => Math.max(0, i - 1))}
            onNextPatient={() => setLightboxIndex((i) => Math.min(apiItems.length - 1, i + 1))}
            hasPrevPatient={lightboxIndex > 0}
            hasNextPatient={lightboxIndex < apiItems.length - 1}
            patientIndex={lightboxIndex}
            totalPatients={apiItems.length}
          />
        )}
        <div className={className}>
          {apiItems.map((item, idx) => (
            <GalleryCard key={item.id} item={item} onOpen={() => setLightboxIndex(idx)} />
          ))}
        </div>
      </>
    );
  }

  // Plain images mode
  if (!images.length) {
    return null;
  }

  return (
    <>
      {activeImage && (
        <div
          className="fixed inset-0 z-[120] bg-[#808080] flex items-center justify-center px-4 py-6"
          onClick={closePopup}
          role="dialog"
          aria-modal="true"
          aria-label="Smile gallery image"
        >
          <button
            type="button"
            onClick={closePopup}
            className="absolute top-6 right-6 w-10 h-10 border border-white/20 text-white flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
            aria-label="Close image preview"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
          <div onClick={(event) => event.stopPropagation()}>
            <img
              src={activeImage}
              alt="Smile gallery preview"
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>
        </div>
      )}

      <div className={className}>
        {images.map((image, index) => (
          <div
            key={`${image}-${index}`}
            className="aspect-square overflow-hidden group cursor-pointer"
            data-testid={`smile-gallery-tile-${index}`}
            onClick={() => setActiveImage(image)}
          >
            <img
              src={image}
              alt=""
              className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
            />
          </div>
        ))}
      </div>
    </>
  );
};